"use client";

import { useEffect, useState, useTransition } from "react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import type { SwitcherLanguage } from "@/components/language-switcher";
import { FlagImage } from "@/components/flag-image";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function SiteLanguageSwitcher({
  languages,
  className,
}: {
  languages: SwitcherLanguage[];
  className?: string;
}) {
  const currentLocale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [mounted, setMounted] = useState(false);
  const current = languages.find((l) => l.code === currentLocale) ?? languages[0];

  useEffect(() => {
    setMounted(true);
  }, []);

  function switchLocale(locale: SwitcherLanguage["code"]) {
    if (locale === currentLocale) return;
    startTransition(() => {
      router.replace(pathname, { locale });
      // Same Router Cache gotcha as the dashboard's cookie-based
      // LanguageSwitcher (components/language-switcher.tsx) — without this,
      // Next.js can reuse the previous locale's RSC payload for the new URL.
      router.refresh();
    });
  }

  if (!languages.length) return null;
  if (!mounted) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            disabled={isPending}
            className={cn(
              "h-8 gap-1.5 rounded-full border border-[#f5c542]/30 bg-white px-2.5 text-sm font-medium text-[#041127] hover:bg-white/90 hover:text-[#041127]",
              className
            )}
          />
        }
      >
        <FlagImage emoji={current?.flag ?? ""} size={18} />
        <span className="hidden sm:inline">{current?.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        {languages.map(({ code, name, flag }) => (
          <DropdownMenuItem
            key={code}
            onClick={() => switchLocale(code)}
            className={currentLocale === code ? "font-medium" : ""}
          >
            <FlagImage emoji={flag} size={18} />
            <span>{name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
